import { useApp } from "../context";
import { Heading, Metric, Table, CaseLink, NewCaseButton } from "../components/Bits";
import { money, sum, expected, received, exposure, outstanding, today, getCase } from "../lib/engine";

const WALKTHROUGH = [
  ["LC-1001", "Fully matched", "Test duplicate advance prevention"],
  ["LC-1002", "Partial bank receipt", "Import the remaining ₹30,000"],
  ["LC-1003", "Overdue commission", "Review collection ownership"],
  ["LC-1004", "Exposure limit breach", "Inspect the blocked advance"],
  ["LC-1005", "Eligible advance", "Request as maker, approve as checker"]
];

const BankBars = () => {
  const { state } = useApp();
  const banks = [...new Set(state.cases.map(c => c.bank))];
  return (
    <div className="card">
      <h2>Collections by bank</h2>
      {banks.map(bank => {
        const cases = state.cases.filter(c => c.bank === bank);
        const exp = sum(cases, expected);
        const actual = sum(cases, c => received(state, c));
        const percent = exp ? Math.round(actual / exp * 100) : 0;
        return (
          <div className="bar-row" key={bank} data-testid={`bank-bar-${bank.replace(/\s+/g, "-").toLowerCase()}`}>
            <span>{bank}</span>
            <div className="track"><div className="fill" style={{ width: `${percent}%` }} /></div>
            <strong>{percent}%</strong>
          </div>
        );
      })}
    </div>
  );
};

const ControlDesk = () => {
  const { state, navigate } = useApp();
  return (
    <div className="card">
      <h2>Control desk</h2>
      <div className="row"><span>Open exceptions</span>
        <strong data-testid="open-exceptions-count">{state.exceptions.filter(e => e.status === "Open").length}</strong></div>
      <div className="row"><span>Pending approvals</span>
        <strong data-testid="pending-approvals-count">{state.payments.filter(p => p.status === "Pending").length}</strong></div>
      <div className="row"><span>Confirmed losses</span><strong>{money(0)}</strong></div>
      <div className="row"><span>Demo partners</span><strong>{state.partners.length}</strong></div>
      <p className="muted">Loss recognition is not automated in this demo.</p>
      <button data-testid="review-exceptions-btn" onClick={() => navigate("Exceptions")}>Review exceptions →</button>
    </div>
  );
};

export default function Dashboard() {
  const { state } = useApp();
  const cases = state.cases;
  return (
    <>
      <Heading title="Every rupee, accounted for."
        description="Loan commissions, DSA advances and bank collections in one workspace.">
        <NewCaseButton />
      </Heading>
      <div className="cards">
        <Metric label="Expected bank commission" value={money(sum(cases, expected))} note="Verified disbursements only" />
        <Metric label="Bank collections" value={money(sum(cases, c => received(state, c)))} note="Receipts allocated to cases" />
        <Metric label="Unsettled DSA advances" value={money(sum(cases, c => exposure(state, c)))} note="Paid advances awaiting full bank receipt" />
        <Metric label="Overdue bank commission"
          value={money(sum(cases.filter(c => c.due < today()), c => outstanding(state, c)))}
          note="Delayed collections, not confirmed losses" />
      </div>
      <div className="grid">
        <BankBars />
        <ControlDesk />
      </div>
      <div className="card space">
        <h2>Interview walkthrough</h2>
        <Table headers={["Case", "Scenario", "Try this"]} testId="walkthrough-table">
          {WALKTHROUGH.filter(([id]) => getCase(state, id)).map(([id, scenario, action]) => (
            <tr key={id}>
              <td><CaseLink c={getCase(state, id)} /></td>
              <td>{scenario}</td>
              <td>{action}</td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  );
}
